import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { t } from 'i18next';
import {
    MANTY_STATUS,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import {
    createRealizedColumn,
    getAmountColumns,
    isRowGrouped,
    isRowPinned,
    createLoadedSimulatedColumn,
    isFooterRow,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { envelopeMantyStatusHypothesisValueGetter } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/NonRecurrentEnvelopesResume/helpers/mantyStatusHypothesisHelpers';
import { SimulationNonRecurrentEnvelopeData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { NonRecurrentDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/NonRecurrentEnvelope/helpers/types';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

const envelopeMantyStatusHypothesisCellRendererSelector = (
    params: ICellRendererParams<SimulationNonRecurrentEnvelopeData>,
) => {
    if (isRowGrouped(params) || isRowPinned(params) || isFooterRow(params))
        return { component: undefined };
    return {
        component:
            params.value !== undefined
                ? 'editEnvelopeMantyStatusHypothesisCellRenderer'
                : 'createEnvelopeMantyStatusHypothesisCellRenderer',
    };
};

export const envelopeMantyStatusHypothesisFilterValueGetter = (
    params: ValueGetterParams,
) => {
    const {
        data,
        context: { envelopeMantyStatusHypotheses },
    } = params;

    const hypotheses: NonRecurrentDataGridContext['envelopeMantyStatusHypotheses'] =
        envelopeMantyStatusHypotheses;
    if (!data) return undefined;
    const hypothesis = hypotheses.find(
        (hyp) =>
            hyp[ORG_CHART_HR_SOFTWARE_ID] === data[ORG_CHART_HR_SOFTWARE_ID] &&
            hyp[MANTY_STATUS] === data[MANTY_STATUS],
    );
    if (hypothesis) {
        return t('humanResources.simulation.editHypothesis');
    }
    return '';
};

export const getColDefs = ({
    hrSimulation,
    envelopeMantyStatusHypotheses,
}: {
    hrSimulation: HrSimulation;
    envelopeMantyStatusHypotheses: NonRecurrentDataGridContext['envelopeMantyStatusHypotheses'];
}): (ColDef | ColGroupDef)[] => {
    const betweenCurrentAndSimulatedDateColumnGetters = [
        createRealizedColumn,
        createLoadedSimulatedColumn,
    ];
    const pastDateColumnGetters = [createRealizedColumn];
    const simulatedDateColumnGetters = [createLoadedSimulatedColumn];
    const columns: (ColDef | ColGroupDef)[] = [
        getGroupedOrgChartHrColumn(),
        {
            ...getMantyStatusColumn(),
            lockPosition: 'left',
            pinned: 'left',
        },
        {
            ...TEXT_FILTER,
            headerName: t('humanResources.simulation.hypothesis'),
            cellRendererSelector:
                envelopeMantyStatusHypothesisCellRendererSelector,
            valueGetter: envelopeMantyStatusHypothesisValueGetter(
                envelopeMantyStatusHypotheses,
            ),
            filterValueGetter: envelopeMantyStatusHypothesisFilterValueGetter,
            cellRendererParams: {
                hrSimulation,
            },
            width: 250,
            lockPosition: 'left',
            pinned: 'left',
            suppressColumnsToolPanel: true,
        },
        ...getAmountColumns({
            simulationStart: hrSimulation.startDate,
            simulationEnd: hrSimulation.endDate,
            betweenCurrentAndSimulatedDateColumnGetters,
            pastDateColumnGetters,
            simulatedDateColumnGetters,
        }),
    ];
    return columns;
};
